import { BehaviorSubject } from "rxjs";
import { AppointmentSettingsEntry } from "@apps/patient-core/src/app/data_model/appointment-settings";
import { PracticeEntry } from "@apps/patient-core/src/app/data_model/practice";
import { Injectable } from "@angular/core";
import { logger } from "@dentally/ui/logger";
import { GRAPHQL_OPERATION_NAMES } from "../http.service";
import { CommonEntry } from "@apps/patient-core/src/app/data_model/common";
import { GAService } from "../ga.service";
import { fromBrandInfoBase } from "@apps/patient-core/src/app/data_model/brand-info";
import { HOUR_IN_MS } from "@shared/time-constants";
import { CacheService } from "../cache.service";
import { Constants } from "@apps/patient-core/constants";
import { LocationService } from "../location.service";
import { FeatureFlagsService } from "../feature-flags.service";
import { BrandInfoBase, BrandInfoSiteBase } from "@backend/graph/brand_info/brand-info-base";
import { environment } from "@apps/patient-core/src/app/environments/environment";
import { NavigationService } from "../navigation.service";
import { AcquisitionSourceEntry } from "@apps/patient-core/src/app/data_model/acquisition-source";
import { filterDefined } from "../../utils/rxjs";
import { CachedHttpService } from "../cached-http.service";
import { LocalisationService } from "../localisation.service";
import { SmileGoalsBase } from "@backend/graph/smile-goals/smile-goals-base";
import { SmileGoalEntry, SmileGoalsMotivationEntry, SmileGoalsServiceEntry, SmileGoalsTimeframeEntry } from "@apps/patient-core/src/app/data_model/smile-goals";
import { BaseBrandService } from "./brand.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { BaseAuthService } from "./auth.service";
import { IFeatureFlags } from "@backend/common/interfaces/feature-flags.interface";

const COMMON_QUERY = (uri: string) => `{
  practice {
    currency_code
    dial_code
    iso_country_code
    hso_region
    time_zone
    phone_number
    is_multisite
    name
    show_acquisition_source
    show_emergency_contact
    show_patient_gp_details
    show_smile_goals
    supports_nhs_pr
    acquisition_sources {
      items {
        id
        name
        show_in_portal
      }
    }
    appointment_settings {
      practice_id
      allow_appointment_cancellations
      show_all_slots_new_patients
      required_to_select_site
      show_all_slots_existing_patients
      min_wait_time
      max_wait_time
      max_days_ahead_bookings
      can_book_together
      hygiene_lapsing_exam_types
    }
    smile_goals {
      goals {
        value
        heading
        description
        motivations {
          value
          heading
        }
      }
      timeframes {
        value
        heading
      }
    }
  }
  brand_info(uri: "${uri}") {
    id
    appointment_list_first
    existing_patients_only_on_brand_url
    ga_tracking_cookie_domain
    ga_tracking_id
    ga_tracking_link_domain
    site_locking_enabled
    sites {
      site_id
      site_name
      site_phone_number
      site_email_address
      site_website
      site_address_line_1
      site_address_line_2
      site_address_line_3
      site_town
      site_county
      site_postcode
      stripe_account_id
      settings {
        forms__nhs_pr
        forms__medical_history
        forms__treamtent_plan_estimates
        payments__balance_payments
      }
    }
  }
}
`;

@Injectable()
export abstract class BaseCommonService {
  private _dataLoaded = false;
  private _commonData: CommonEntry;
  private _onInitData: BehaviorSubject<CommonEntry | null> = new BehaviorSubject(null);
  private _isFetching: boolean;
  constructor(
    protected _brandService: BaseBrandService,
    protected _patientsService: BasePatientsService,
    protected _authService: BaseAuthService,
    protected _gaService: GAService,
    protected _cacheService: CacheService,
    protected _locationService: LocationService,
    protected _featureFlagsService: FeatureFlagsService,
    protected _navigationService: NavigationService,
    protected _cachedHttpService: CachedHttpService,
    protected _localisationService: LocalisationService
  ) {}

  public get onInitData() {
    return this._onInitData.pipe(filterDefined());
  }

  public get isCommonDataLoaded(): boolean {
    return this._dataLoaded;
  }

  public get practice(): PracticeEntry {
    return this._commonData.practice;
  }

  public get max_days_ahead_bookings(): number {
    return this._commonData.practice.appointment_settings.max_days_ahead_bookings;
  }

  public get hso_region(): string {
    return this._commonData.practice.hso_region;
  }

  public get time_zone(): string {
    return this._commonData.practice.time_zone;
  }

  public get siteCountyCodeForPatient(): string | undefined {
    const patientSiteId = this._patientsService.patientInfo?.site_id;
    const sites = this._commonData.brand_info?.sites;

    if (patientSiteId) {
      const matchingSite = sites?.find((site) => site.site_id === patientSiteId);
      if (matchingSite) {
        //use the county on the patient's site is available
        return matchingSite.site_county;
      }
    }

    //otherwise just use the county from the first site
    return sites?.[0]?.site_county;
  }

  public get urlSiteId(): string {
    return this._brandService.restrictedSiteId;
  }

  public getSitePhoneNumber(siteId: string): string | null {
    const site = this._commonData.brand_info.sites.find((s) => s.site_id === siteId);
    if (site) return site.site_phone_number;
    return null;
  }

  public getSite(site_id: string): BrandInfoSiteBase | undefined {
    return this._commonData.brand_info.sites.find((site) => site.site_id === site_id);
  }

  protected abstract get allowAutocomplete(): boolean;

  protected _handleDomainInfo200(
    graphData: BrandInfoBase & { code?: number; redirect_uri?: string; feature_flags: IFeatureFlags; iso_country_code: string },
    _pathname: string
  ): void {
    delete graphData.code;
    delete graphData.redirect_uri;
    const { feature_flags, iso_country_code } = graphData;

    this._cacheService.setJson(Constants.BRAND_INFO_STORAGE_KEY, graphData, this._cacheService.getRelativeTtl(HOUR_IN_MS));

    this._featureFlagsService.setFeatureFlags(feature_flags);
    this._brandService.setupBrandingConfig(graphData);

    this._localisationService.country_code = iso_country_code;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected _handleDomainInfo302(graphData: any, pathname: string): void {
    window.open(`https://${graphData.redirect_uri}/${(pathname || "").replace(/^\//, "")}`, "_self");
  }

  public getDomainInfo(): Promise<void> {
    const { hostname, pathname } = this._locationService;
    let url = `${environment.REST_URL}/api/domains/info?uri=${hostname}`;

    if (this._locationService.search.referrer) {
      url += `&referrer=${this._locationService.search.referrer}`;
    }

    return new Promise<void>((resolve, reject) => {
      this._cachedHttpService.send(url, { method: "GET" }, { notPatientSpecific: true, ttl: 3600 }).subscribe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (graphData: any) => {
          switch (graphData.code) {
            case 200:
              this._handleDomainInfo200(graphData, pathname);
              break;

            case 302:
              this._handleDomainInfo302(graphData, pathname);
              // Reject the promise to prevent the app from loading
              reject(302);
              return;

            case 404:
              this._handleDomainInfo404(graphData);
              // Reject the promise to prevent the app from loading and allow the error or pair routes to load correctly
              reject(404);
              return;

            default:
              throw new Error(`Unhandled error code ${graphData.code} from domains/info`);
          }
          resolve();
        },
        (err) => {
          this._navigationService.navigate("error");
          reject(err);
        }
      );
    });
  }

  protected _handleDomainInfo404(graphData: { code: number }) {
    logger.error("Unknown error code getting brand info", { code: "e15f44b0", httpCode: graphData.code });
    this._navigationService.navigate("error");
  }

  public getCommonData(force = false): void {
    const jwt = this._authService.getJWT();

    // If we are already fetching, then wait until the fetch is complete and resolve the data
    if (this._isFetching) {
      const interval = setInterval(() => {
        if (!this._isFetching) {
          clearInterval(interval);
        }
      }, 300);
      return;
    }

    if (!force && this._dataLoaded) {
      this._onInitData.next(this._commonData);
      this._dataLoaded = true;
      return;
    }
    if (jwt) {
      this._isFetching = true;

      this._cachedHttpService
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .query<any>(GRAPHQL_OPERATION_NAMES.COMMON, COMMON_QUERY(this._locationService.hostname), { notPatientSpecific: true, ttl: 3600 })
        .subscribe(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (response: any) => {
            if (response.errors) {
              logger.error("Error fetching common data", { errors: response.errors });
              this._onInitData.error({ errors: response.errors });
              return;
            }

            const common_entry = new CommonEntry();
            const { practice } = response.data;

            if (!practice) {
              return;
            }

            // Setup entries
            const practice_entry = new PracticeEntry(practice);
            const appointment_settings_entry = new AppointmentSettingsEntry(practice.appointment_settings);
            const acquisition_sources = new Array<AcquisitionSourceEntry>();

            if (practice.acquisition_sources.items) {
              for (const source of practice.acquisition_sources.items) acquisition_sources.push(new AcquisitionSourceEntry(source));
              acquisition_sources.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "accent" }));
            }

            // Setup practice data
            if (!practice_entry.iso_country_code) practice_entry.iso_country_code = "GB";
            if (!practice_entry.time_zone) practice_entry.time_zone = "Europe/London";
            if (!practice_entry.currency_code) practice_entry.currency_code = "GBP";
            if (!practice_entry.dial_code) practice_entry.dial_code = "44";
            practice_entry.appointment_settings = appointment_settings_entry;
            practice_entry.acquisition_sources.items = acquisition_sources;
            const smile_goal_data = this._setupSmileGoals(practice.smile_goals, practice.show_smile_goals);
            practice_entry.smile_goals = smile_goal_data;

            // Setup common data
            this._commonData = common_entry;
            this._commonData.practice = practice_entry;
            this._commonData.brand_info = response.data.brand_info;
            this._gaService.setup(response.data.brand_info);
            this._brandService.brand = fromBrandInfoBase(response.data.brand_info, this._patientsService.patientInfo);

            this._onInitData.next(this._commonData);
            this._dataLoaded = true;
            this._isFetching = false;
          },
          (err) => {
            logger.error("Error fetching common data", { error: err.message || err });
            this._onInitData.error(err);
          }
        );
    } else {
      this._onInitData.error("No JWT");
    }
  }

  private _setupSmileGoals(smile_goals: SmileGoalsBase, show_smile_goals: boolean): SmileGoalEntry {
    if (!show_smile_goals || !smile_goals) {
      return {
        goals: [],
        timeframes: [],
      };
    }

    const { goals, timeframes } = smile_goals;
    const goal_entries = new Array<SmileGoalsServiceEntry>();
    const timeframe_entries = new Array<SmileGoalsTimeframeEntry>();

    for (const goal of goals) {
      const motivation_entries = new Array<SmileGoalsMotivationEntry>();
      for (const motivation of goal.motivations) {
        motivation_entries.push(new SmileGoalsMotivationEntry(motivation));
      }
      goal.motivations = motivation_entries;
      goal_entries.push(new SmileGoalsServiceEntry(goal));
    }

    for (const timeframe of timeframes) {
      timeframe_entries.push(new SmileGoalsTimeframeEntry(timeframe));
    }

    return {
      goals: goal_entries,
      timeframes: timeframe_entries,
    };
  }
}
